import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const UploadDiagnosticsPanel = () => {
  const [diagnostics, setDiagnostics] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [queryResults, setQueryResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dedupAnalysis, setDedupAnalysis] = useState(null);
  const [facilityStats, setFacilityStats] = useState([]);
  const [recentArrestsStats, setRecentArrestsStats] = useState(null);

  useEffect(() => {
    loadInitialDiagnostics();
  }, []);

  const loadInitialDiagnostics = async () => {
    setLoading(true);
    try {
      // Load last 15 uploads with diagnostic info
      await runDiagnosticQuery('recent_uploads');
      
      // Load dedup analysis
      await loadDedupAnalysis();
      
      // Load facility stats
      await loadFacilityStats();
      
      // Load recent arrests summary
      await loadRecentArrestsStats();
    } catch (err) {
      setError(`Failed to load diagnostics: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runDiagnosticQuery = async (queryType, filename = '') => {
    setLoading(true);
    setError('');
    
    try {
      let result;
      
      switch (queryType) {
        case 'recent_uploads':
          result = await supabase?.rpc('get_upload_diagnostics', { limit_count: 15 });
          if (result?.error) throw result?.error;
          setDiagnostics(result?.data || []);
          setQueryResults({
            type: 'recent_uploads',
            title: 'Last 15 Uploads with Diagnostic Counts',
            data: result?.data || []
          });
          break;
          
        case 'specific_upload':
          if (!filename?.trim()) {
            throw new Error('Please enter a filename pattern');
          }
          
          // Get upload details
          const { data: uploadData, error: uploadError } = await supabase?.from('upload_batches')?.select('*')?.ilike('filename', `%${filename}%`)?.order('upload_date', { ascending: false })?.limit(1)?.single();
            
          if (uploadError) throw uploadError;
          
          // Get error details if rows_inserted = 0
          let errorDetails = [];
          if (uploadData?.rows_inserted === 0 && uploadData?.rows_errors > 0) {
            const errorResult = await supabase?.rpc('get_upload_errors', {
              batch_filename: filename,
              error_limit: 5
            });
            if (!errorResult?.error) {
              errorDetails = errorResult?.data || [];
            }
          }
          
          setQueryResults({
            type: 'specific_upload',
            title: `Upload Details for: ${filename}`,
            data: uploadData,
            errors: errorDetails
          });
          break;
          
        case 'dedup_check':
          const { data: dedupData, error: dedupError } = await supabase?.from('arrests')?.select('COUNT(*) as total, COUNT(DISTINCT row_hash) as distinct_hashes')?.gte('uploaded_at', new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)?.toISOString());
            
          if (dedupError) throw dedupError;
          
          const stats = dedupData?.[0];
          const collisionRate = stats?.total > 0 ? 
            ((stats?.total - stats?.distinct_hashes) / stats?.total * 100)?.toFixed(2) : 0;
            
          setQueryResults({
            type: 'dedup_check',
            title: 'Deduplication Hash Analysis (Last 2 Days)',
            data: {
              total: stats?.total,
              distinct_hashes: stats?.distinct_hashes,
              collision_rate: collisionRate,
              potential_issue: collisionRate > 20
            }
          });
          break;
          
        default:
          throw new Error('Unknown query type');
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDedupAnalysis = async () => {
    try {
      let result = await supabase?.rpc('analyze_dedup_collisions', { days_back: 2 });
      if (!result?.error && result?.data?.[0]) {
        setDedupAnalysis(result?.data?.[0]);
      }
    } catch (err) {
      console.warn('Failed to load dedup analysis:', err);
    }
  };

  const loadFacilityStats = async () => {
    try {
      let result = await supabase?.rpc('get_facility_population_summary');
      if (!result?.error) {
        setFacilityStats(result?.data || []);
      }
    } catch (err) {
      console.warn('Failed to load facility stats:', err);
    }
  };

  const loadRecentArrestsStats = async () => {
    try {
      let result = await supabase?.rpc('get_recent_arrests_summary', { hours_back: 48 });
      if (!result?.error && result?.data?.[0]) {
        setRecentArrestsStats(result?.data?.[0]);
      }
    } catch (err) {
      console.warn('Failed to load recent arrests stats:', err);
    }
  };

  const getDiagnosticSummary = (upload) => {
    if (!upload) return 'No data';
    
    const { rows_in_file, rows_inserted, rows_skipped_duplicates, rows_errors } = upload;
    
    if (rows_inserted === 0 && rows_in_file > 0) {
      return `❌ ZERO RECORDS: ${rows_in_file} in file, ${rows_errors} errors, ${rows_skipped_duplicates} duplicates`;
    } else if (rows_errors > 0) {
      return `⚠️ WITH ERRORS: ${rows_inserted} inserted, ${rows_errors} errors, ${rows_skipped_duplicates} duplicates`;
    } else {
      return `✅ SUCCESS: ${rows_inserted} inserted, ${rows_skipped_duplicates} duplicates`;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Upload Diagnostics & SQL Triage</h3>
          <p className="text-sm text-muted-foreground">
            Comprehensive diagnostic tools for troubleshooting zero-record uploads
          </p>
        </div>
        
        {/* Status Indicators */}
        <div className="flex items-center space-x-3">
          {dedupAnalysis && (
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs ${
              dedupAnalysis?.potential_issue 
                ? 'bg-red-100 text-red-700 border border-red-200' :'bg-green-100 text-green-700 border border-green-200'
            }`}>
              <Icon 
                name={dedupAnalysis?.potential_issue ? 'AlertTriangle' : 'CheckCircle'} 
                size={12} 
              />
              <span>
                Hash Collision: {dedupAnalysis?.collision_rate}%
                {dedupAnalysis?.potential_issue && ' (High Risk)'}
              </span>
            </div>
          )}
          
          {recentArrestsStats && (
            <div className="flex items-center space-x-2 px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-700 border border-blue-200">
              <Icon name="TrendingUp" size={12} />
              <span>
                {recentArrestsStats?.total_new_arrests} arrests (48h)
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Quick SQL Triage Buttons */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-foreground mb-3">Quick SQL Triage:</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button
            onClick={() => runDiagnosticQuery('recent_uploads')}
            disabled={loading}
            className="flex items-center space-x-2 justify-start"
            variant="outline"
          >
            <Icon name="List" size={16} />
            <span>Last 15 Uploads</span>
          </Button>
          
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="e.g., 08272025"
              value={selectedFile}
              onChange={(e) => setSelectedFile(e?.target?.value)}
              className="flex-1 px-3 py-2 border border-border rounded text-sm"
              disabled={loading}
            />
            <Button
              onClick={() => runDiagnosticQuery('specific_upload', selectedFile)}
              disabled={loading || !selectedFile?.trim()}
              size="sm"
            >
              <Icon name="Search" size={16} />
            </Button>
          </div>
          
          <Button
            onClick={() => runDiagnosticQuery('dedup_check')}
            disabled={loading}
            className="flex items-center space-x-2 justify-start"
            variant="outline"
          >
            <Icon name="Hash" size={16} />
            <span>Dedup Analysis</span>
          </Button>
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Query Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mr-3"></div>
          <span className="text-muted-foreground">Running diagnostic query...</span>
        </div>
      )}
      {/* Query Results */}
      {queryResults && !loading && (
        <div className="mb-6 p-4 bg-muted/30 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">{queryResults?.title}</h4>
          
          {queryResults?.type === 'recent_uploads' && (
            <div className="space-y-2">
              {queryResults?.data?.map((upload, index) => (
                <div key={index} className="p-3 bg-card rounded border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">{upload?.filename}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(upload.upload_date)?.toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {getDiagnosticSummary(upload)}
                  </div>
                  <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                    <div>In File: <span className="font-medium">{upload?.rows_in_file || 0}</span></div>
                    <div>Inserted: <span className="font-medium text-accent">{upload?.rows_inserted || 0}</span></div>
                    <div>Duplicates: <span className="font-medium text-warning">{upload?.rows_skipped_duplicates || 0}</span></div>
                    <div>Errors: <span className="font-medium text-error">{upload?.rows_errors || 0}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {queryResults?.type === 'specific_upload' && (
            <div className="space-y-3">
              <div className="p-3 bg-card rounded border">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-foreground">File:</span>
                    <p className="text-muted-foreground">{queryResults?.data?.filename}</p>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Upload Date:</span>
                    <p className="text-muted-foreground">
                      {new Date(queryResults.data.upload_date)?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Status:</span>
                    <p className="text-muted-foreground">{queryResults?.data?.status}</p>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Processing Mode:</span>
                    <p className="text-muted-foreground">{queryResults?.data?.processing_mode || 'normal'}</p>
                  </div>
                </div>
                
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <p className="font-medium text-foreground">{queryResults?.data?.rows_in_file || 0}</p>
                    <p className="text-xs text-muted-foreground">Rows in File</p>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <p className={`font-medium ${queryResults?.data?.rows_inserted > 0 ? 'text-accent' : 'text-error'}`}>
                      {queryResults?.data?.rows_inserted || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Inserted</p>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <p className="font-medium text-warning">{queryResults?.data?.rows_skipped_duplicates || 0}</p>
                    <p className="text-xs text-muted-foreground">Duplicates</p>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <p className={`font-medium ${queryResults?.data?.rows_errors > 0 ? 'text-error' : 'text-muted-foreground'}`}>
                      {queryResults?.data?.rows_errors || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Errors</p>
                  </div>
                </div>
                
                {queryResults?.data?.error_message && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm font-medium text-red-800">Error Summary:</p>
                    <p className="text-sm text-red-700">{queryResults?.data?.error_message}</p>
                  </div>
                )}
              </div>
              
              {/* First 5 Error Messages for Zero Insert Cases */}
              {queryResults?.errors && queryResults?.errors?.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <h5 className="text-sm font-medium text-red-800 mb-2">
                    First {Math.min(queryResults?.errors?.length, 5)} Error Messages:
                  </h5>
                  <div className="space-y-2">
                    {queryResults?.errors?.slice(0, 5)?.map((error, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-red-800">
                          Line {error?.line_number}:
                        </span>
                        <span className="text-red-700 ml-2">{error?.error_message}</span>
                        {error?.raw_value && (
                          <div className="text-xs text-red-600 font-mono mt-1">
                            Raw: {error?.raw_value}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {queryResults?.type === 'dedup_check' && (
            <div className="p-3 bg-card rounded border">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-center">
                <div className="p-2 bg-muted/50 rounded">
                  <p className="font-medium text-foreground">{queryResults?.data?.total}</p>
                  <p className="text-xs text-muted-foreground">Total Arrests</p>
                </div>
                <div className="p-2 bg-muted/50 rounded">
                  <p className="font-medium text-foreground">{queryResults?.data?.distinct_hashes}</p>
                  <p className="text-xs text-muted-foreground">Distinct Hashes</p>
                </div>
                <div className="p-2 bg-muted/50 rounded">
                  <p className={`font-medium ${queryResults?.data?.collision_rate > 20 ? 'text-error' : 'text-accent'}`}>
                    {queryResults?.data?.collision_rate}%
                  </p>
                  <p className="text-xs text-muted-foreground">Collision Rate</p>
                </div>
                <div className="p-2 bg-muted/50 rounded">
                  <p className={`font-medium ${queryResults?.data?.potential_issue ? 'text-error' : 'text-accent'}`}>
                    {queryResults?.data?.potential_issue ? 'Yes' : 'No'}
                  </p>
                  <p className="text-xs text-muted-foreground">Hash Issue</p>
                </div>
              </div>
              
              {queryResults?.data?.potential_issue && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm font-medium text-red-800">⚠️ Row Hash Issue Detected</p>
                  <p className="text-sm text-red-700">
                    High collision rate suggests insufficient row hash fields. 
                    The current hash may only use (name, dob) instead of full record fields.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Facility Population Summary */}
      {facilityStats?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">Current Population by Facility:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {facilityStats?.slice(0, 6)?.map((facility, index) => (
              <div key={index} className="p-3 bg-muted/30 rounded border">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">
                    {facility?.facility_id || 'Unknown'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(facility.latest_upload)?.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-accent font-medium">{facility?.in_custody_count} IN_CUSTODY</span>
                  <span className="text-muted-foreground">{facility?.total_arrests} total</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Common Error Messages Reference */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">Common Error Messages to Surface:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-yellow-700">
          <div>
            <p className="font-medium mb-1">Encoding Issues:</p>
            <p>• "EncodingError: non-UTF8 byte at line X; retried cp1252 ok"</p>
            <p>• "BOM detected and will be stripped"</p>
          </div>
          <div>
            <p className="font-medium mb-1">Structure Issues:</p>
            <p>• "HeaderMismatch: expected 44 columns; saw 46 at line 32"</p>
            <p>• "Missing columns: found 40, expected 44"</p>
          </div>
          <div>
            <p className="font-medium mb-1">Data Issues:</p>
            <p>• "DateParseError: ArrDate '8/1/25 14:22' invalid format"</p>
            <p>• "ValidationError: missing required field first_name"</p>
          </div>
          <div>
            <p className="font-medium mb-1">Dedup Issues:</p>
            <p>• "DedupConflict: row_hash collision suggests insufficient hash fields"</p>
            <p>• "All records matched existing entries - possible hash collision"</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDiagnosticsPanel;